import { Component, effect, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IMessage } from '@boldpenguin/sdk';
import { Actions } from 'bqx-deal';
import { LoggerAbstractService, LogLevel } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationFormState, ApplicationQuestionSetCodes, SDKSessionItem } from 'src/app/enums/sdk-application.enum';
import { IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { Global } from 'src/app/models/shared/app-global.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { AbstractDealAnalyticsService } from 'src/app/services/deal-analytics/abstract-deal-analytics.service';

@Component({
    selector: 'appform-header',
    templateUrl: './application-form-header.component.html',
    styleUrls: ['./application-form-header.component.css'],
    standalone: false
})
export class ApplicationFormHeaderComponent implements OnInit {
  @Input() activeMenuOrder: number;
  @Output() modeChange = new EventEmitter<number>();
  public readOnlyMode = false;
  private _reference_id: string;
  private _unsubAppForm$ = new Subject<void>();
  private _appFormState: string;
  private _isOwner: boolean;
  private _isLocked: boolean;
  private _isTakenOver: boolean;
  private _isAppSyncedWithBP: boolean;
  private _activeMode: string;
  private _questionSets: IQuestionSet[];
  private _getMessages: IMessage[] = [];

  readonly _ADDITIONAL_CONSENT_MESSAGE = 'To Revoke TCPA Consent please call Bold Penguin at 888-949-6778';
  readonly _CONSENT_QUESTION_CODE = 'progressive_send_to_bold_penguin';

  public get referenceId(): string {
    return this._reference_id ? this._reference_id : Global.applicationId;
  }

  public get isTakenOver(): boolean {
    return this._isTakenOver;
  }

  private get isQuoted(): boolean {
    return this._appFormState === ApplicationFormState.QUOTED;
  }

  private get isCompleted(): boolean {
    return this._appFormState === ApplicationFormState.COMPLETED;
  }

  public get getMessages(): IMessage[] {
    return this._getMessages;
  }

  public get headerMode(): string {
    if (this._isAppSyncedWithBP) {
      if (this._isLocked && !this.applicationFormService.isReferred) {
        return 'locked';
      } else if (!this._isOwner && !this._isTakenOver && (!this._isLocked || this.applicationFormService.isReferred)) {
        this.readOnlyMode = true;
        this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);
        return 'takeover';
      }
    }
    return 'normal';
  }
  public get consentQuestionSet(): IQuestionSet | null {
    const questionSet = this.applicationFormService.getQuestionSet(ApplicationQuestionSetCodes.CONSENT);
    return questionSet ? questionSet : null;
  }

  public get incompleteEOMQuestionSet(): IQuestionSet | null {
    const questionSets = this._questionSets.filter((queSet) => !queSet.is_completed);
    return questionSets ? questionSets[0] : null;
  }

  private _oldApplicationId = '';
  private applicationIdSignal = signal('');
  constructor(
    private applicationFormService: ApplicationFormAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private router: Router,
    private dealAnalyticsService: AbstractDealAnalyticsService
  ) {
    this.onQuoteNumberChange();
  }

  private onQuoteNumberChange() {
    effect(() => {
      const newApplicationId = this.applicationIdSignal();
      if (this._oldApplicationId !== '' && newApplicationId !== this._oldApplicationId && this.referenceId) {
        this.dealAnalyticsService.postDealPageEvent(null, null, this._oldApplicationId);
        this.applicationFormService.isHandshakeDealCall = true;
      }
    });
  }

  ngOnInit(): void {
    this.applicationFormService.application$.pipe(takeUntil(this._unsubAppForm$)).subscribe(
      (form) => {
        if (form) {
          if (!sessionStorage.getItem(SDKSessionItem.UID_TOKEN)) {
            this._oldApplicationId = form.form.id;
          }
          this.applicationIdSignal.set(form.form.id);

          this._reference_id = form.form.reference_id;
          Global.applicationId = form.form.id;
          this._appFormState = form.form.state;
          this._questionSets = form.questionSets.list;
          if (this.isQuoted || this.isCompleted || this.applicationFormService.isReferred) {
            this.readOnlyMode = this._activeMode === 'editMode' ? false : true;
          } else {
            this.readOnlyMode = false;
            this._activeMode = null;
          }
          this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);

          if (this.consentQuestionSet) {
            if (this.consentQuestionSet.is_completed === false) {
              this.readOnlyMode = false;
              this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);
            } else {
              this.readOnlyMode = true;
              this.applicationFormService.isAppFormRedirected$.next(true);
              this.applicationFormService.isAppSyncedWithBP$.next(true);
              this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);
            }
          }
          if (this.incompleteEOMQuestionSet && sessionStorage.getItem('EOM') === 'Y') {
            this.readOnlyMode = false;
            this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);
            this.activeMenuOrder = this.incompleteEOMQuestionSet.order;
            this.applicationFormService.isAppFormRedirected$.next(true);
            this.applicationFormService.isAppSyncedWithBP$.next(true);
            sessionStorage.removeItem('EOM');
          }
          if (sessionStorage.getItem('isTakenOver') === 'true' && this._reference_id) {
            this.loggerService.postLogData(LogLevel.Info, 'Takeover Successful', JSON.stringify({ quoteNumber: this._reference_id }));
            sessionStorage.removeItem('isTakenOver');
          }
          if (this._reference_id) {
            this.logSearchView();
          }
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-header: ngOnInit', err, '');
        this._unsubAppForm$.next();
        this._unsubAppForm$.complete();
      }
    );
    this.applicationFormService.isOwner$.subscribe((val) => {
      this._isOwner = val;
    });
    this.applicationFormService.isLocked$.subscribe((val) => {
      this._isLocked = val;
    });
    this.applicationFormService.isTakenOver$.subscribe((val) => {
      this._isTakenOver = val;
    });
    this.applicationFormService.isAppSyncedWithBP$.subscribe((val) => {
      this._isAppSyncedWithBP = val;
    });
    this.applicationFormService.selectLegacyMessages$.subscribe((val) => {
      if (val?.length) {
        this._getMessages = [...val];
        if (this.getMessages.length > 0 && this.isTransferConsentGiven(this.consentQuestionSet)) {
          this.addAdditionalMessage(this.getMessages[this.getMessages.length - 1]);
        }
      }
    });
  }

  public headerButtonToggle(mode: string): void {
    this.dealAnalyticsService.postDealControlEvent(Actions.ButtonClick, this.getHeaderButtonLabelForDeal(mode));
    if ((this.isQuoted || this.isCompleted || this.applicationFormService.isReferred) && this.readOnlyMode) {
      this.applicationFormService.changeActiveQuestionSet(1);
      this.modeChange.emit(1);
    } else if ((this.isQuoted || this.isCompleted || this.applicationFormService.isReferred) && !this.readOnlyMode) {
      if (this.consentQuestionSet?.is_completed === false) {
        this.applicationFormService.changeActiveQuestionSet(this.activeMenuOrder);
        this.modeChange.emit(this.activeMenuOrder);
      } else {
        this.applicationFormService.changeActiveQuestionSet(0);
        this.modeChange.emit(0);
      }
    }
    if (this.headerMode === 'takeover') {
      //  Sets the mqs_coverage_start_date to 'is_completed: false' for takeovers that are outdated
      if (!this.isQuoted) {
        let startDateAnswer = this.applicationFormService.getAnswerByCode('mqs_coverage_start_date');
        if (startDateAnswer && startDateAnswer.value < startDateAnswer.question.min_date) {
          startDateAnswer = { ...startDateAnswer, is_completed: false };
          this.applicationFormService.updateAnswer(startDateAnswer);
        }
      }
      this.loggerService.postLogData(LogLevel.Info, 'Takeover Started', JSON.stringify({ quoteNumber: this._reference_id }));
      this.applicationFormService.isInTransition = true;
      this.applicationFormService.cloneApplicationForm({ appFormId: Global.applicationId });
      //need to clear the appid from the query params so that on refresh the correct id is being used
      //otherwise the application will not have the correct state but an older state
      this.router.navigate([], {
        queryParams: { appid: null },
        queryParamsHandling: 'merge'
      });
      sessionStorage.setItem('isTakenOver', 'true');
    } else {
      this.readOnlyMode = !this.readOnlyMode;
      this.applicationFormService.readOnlyMode$.next(this.readOnlyMode);
    }

    this._activeMode = mode;
  }

  private getHeaderButtonLabelForDeal(mode: string): string {
    if (this.headerMode === 'takeover') {
      return 'Take Over';
    }
    if (mode === 'editMode') {
      return 'Edit';
    }
    return 'View - Quote';
  }

  private logSearchView(): void {
    if (this.applicationFormService.searchPageUser) {
      const result = {
        quoteNumber: this._reference_id,
        user: this.applicationFormService.searchPageUser,
        guid: this.applicationFormService.searchPageGUID
      };
      this.loggerService.postLogData(LogLevel.Info, 'Application Loaded from Search Page', JSON.stringify(result), '');
      this.applicationFormService.searchPageUser = null;
      this.applicationFormService.searchPageGUID = null;
    }
  }

  private addAdditionalMessage(message: IMessage): void {
    const newMessage: IMessage = { ...message, target: this._ADDITIONAL_CONSENT_MESSAGE, id: null };
    this._getMessages.push(newMessage);
  }

  private isTransferConsentGiven(consentQuestionSet: IQuestionSet): boolean {
    if (consentQuestionSet && consentQuestionSet.is_completed) {
      const consentAnswer = consentQuestionSet.answers.find((answer) => answer.question?.code === this._CONSENT_QUESTION_CODE);
      const choiceSelected = consentAnswer?.question?.choices?.find((choice) => choice.id === consentAnswer.choice_id);
      return choiceSelected?.value?.toLowerCase() === 'yes';
    }
    return false;
  }
}
